import { useState, useEffect } from "react";
import {
  useListTimetables,
  useCreateTimetable,
  useDeleteTimetable,
  useGetTimetable,
  useCreateTimetableWeek,
  useSaveTimetableSlots,
  useListCourses,
  useListClasses,
  useCreateCourse,
  useCreateClass,
  getListTimetablesQueryKey,
  getGetTimetableQueryKey,
} from "@workspace/api-client-react";
import { useCopyTimetableFrom } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, ChevronRight, Settings2, ArrowLeft, X, Copy, GripVertical, CopyCheck, ChevronUp, ChevronDown, Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  DndContext, DragEndEvent, closestCenter,
  PointerSensor, TouchSensor, useSensor, useSensors, DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import { format } from "date-fns";

// ─── Types ────────────────────────────────────────────────────────────────────

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday"] as const;
type Day = typeof DAYS[number];
const DAY_LABELS: Record<Day, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday",
};

interface DaySlot {
  id: string;
  type: "period" | "break";
  label: string;
  courseId: number | null;
  classId: number | null;
}

type DayColumns = Record<Day, DaySlot[]>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

// Re-index auto-generated "Period N" labels so they always match visual order after
// a drag/reorder or delete.  Custom labels (anything not matching /^Period \d+$/) are
// left untouched.  Break slots are skipped entirely.
const renumberPeriods = (slots: DaySlot[]): DaySlot[] => {
  let n = 0;
  return slots.map((s) => {
    if (s.type !== "period") return s;
    n++;
    return /^Period \d+$/.test(s.label) ? { ...s, label: `Period ${n}` } : s;
  });
};

const defaultDaySlots = (): DaySlot[] => [
  { id: makeId(), type: "break",  label: "Before School", courseId: null, classId: null },
  { id: makeId(), type: "period", label: "Period 1",      courseId: null, classId: null },
  { id: makeId(), type: "period", label: "Period 2",      courseId: null, classId: null },
  { id: makeId(), type: "period", label: "Period 3",      courseId: null, classId: null },
  { id: makeId(), type: "break",  label: "Lunch",         courseId: null, classId: null },
  { id: makeId(), type: "period", label: "Period 4",      courseId: null, classId: null },
  { id: makeId(), type: "period", label: "Period 5",      courseId: null, classId: null },
];

const defaultDayColumns = (): DayColumns =>
  Object.fromEntries(DAYS.map((d) => [d, defaultDaySlots()])) as DayColumns;

function buildColumnsFromSlots(slots: any[]): DayColumns {
  const cols: DayColumns = Object.fromEntries(DAYS.map((d) => [d, [] as DaySlot[]])) as DayColumns;
  const sorted = [...slots].sort((a, b) => a.position - b.position);
  for (const slot of sorted) {
    if (!DAYS.includes(slot.day as Day)) continue;
    cols[slot.day as Day].push({
      id: `${slot.day}-${slot.id ?? makeId()}`,
      type: slot.slotType as "period" | "break",
      label: slot.label,
      courseId: slot.courseId ?? null,
      classId:  slot.classId  ?? null,
    });
  }
  for (const day of DAYS) {
    if (cols[day].length === 0) cols[day] = defaultDaySlots();
  }
  return cols;
}

// ─── Cell edit dialog ─────────────────────────────────────────────────────────

interface EditCellState { day: Day; slotId: string; title: string; }

// Discriminated union: an existing option, a pending-create name, or nothing.
type ComboboxValue =
  | { type: "existing"; id: number }
  | { type: "new"; name: string }
  | { type: "none" };

// Inline combobox: filter existing options + offer to create a new one by name.
// Does NOT auto-select anything — blank is always a valid choice.
function InlineCombobox({
  value,
  onChange,
  options,
  placeholder,
  createPrefix,
}: {
  value: ComboboxValue;
  onChange: (v: ComboboxValue) => void;
  options: { id: number; name: string; color?: string | null }[];
  placeholder: string;
  createPrefix: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );
  const exactMatch = options.some(
    (o) => o.name.toLowerCase() === search.trim().toLowerCase(),
  );
  const canCreate = search.trim().length > 0 && !exactMatch;

  const selectedOption =
    value.type === "existing" ? options.find((o) => o.id === value.id) ?? null : null;

  const triggerLabel =
    value.type === "existing"
      ? selectedOption?.name ?? "Unknown"
      : value.type === "new"
        ? `"${value.name}" — new`
        : placeholder;

  const triggerColor =
    value.type === "existing" ? selectedOption?.color ?? null : value.type === "new" ? "#94a3b8" : null;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal text-sm h-9",
            value.type === "none" && "text-muted-foreground",
          )}
        >
          <span className="flex items-center gap-2 min-w-0 flex-1 truncate">
            {triggerColor && (
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: triggerColor }}
              />
            )}
            <span className="truncate">{triggerLabel}</span>
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search or type a new name…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandItem
              onSelect={() => {
                onChange({ type: "none" });
                setSearch("");
                setOpen(false);
              }}
            >
              <span className="text-muted-foreground italic">— No {createPrefix} —</span>
            </CommandItem>

            {filtered.length > 0 && (
              <CommandGroup>
                {filtered.map((opt) => (
                  <CommandItem
                    key={opt.id}
                    value={opt.id.toString()}
                    onSelect={() => {
                      onChange({ type: "existing", id: opt.id });
                      setSearch("");
                      setOpen(false);
                    }}
                  >
                    <span className="flex items-center gap-2 flex-1 min-w-0">
                      {opt.color && (
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: opt.color }}
                        />
                      )}
                      <span className="truncate">{opt.name}</span>
                    </span>
                    {value.type === "existing" && value.id === opt.id && (
                      <Check className="h-4 w-4 flex-shrink-0" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {canCreate && (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    onChange({ type: "new", name: search.trim() });
                    setSearch("");
                    setOpen(false);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1 flex-shrink-0 text-primary" />
                  <span>
                    Create {createPrefix}{" "}
                    <strong className="font-semibold">"{search.trim()}"</strong>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {filtered.length === 0 && !canCreate && (
              <CommandEmpty>No {createPrefix}s found.</CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CellEditDialog({
  state, onClose, onSave, courses, classes, currentAssignment,
}: {
  state: EditCellState | null;
  onClose: () => void;
  onSave: (courseId: number | null, classId: number | null) => void;
  courses: { id: number; name: string; color: string; schoolId?: number | null }[];
  classes: { id: number; name: string; schoolId?: number | null; courseIds?: number[] }[];
  currentAssignment: { courseId: number | null; classId: number | null };
}) {
  const { toast }   = useToast();
  const queryClient = useQueryClient();
  const createCourse = useCreateCourse();
  const createClass  = useCreateClass();

  const [courseVal, setCourseVal] = useState<ComboboxValue>({ type: "none" });
  const [classVal,  setClassVal]  = useState<ComboboxValue>({ type: "none" });
  const [isSaving,  setIsSaving]  = useState(false);

  useEffect(() => {
    const cId  = currentAssignment.courseId;
    const clId = currentAssignment.classId;
    setCourseVal(cId  != null ? { type: "existing", id: cId  } : { type: "none" });
    setClassVal( clId != null ? { type: "existing", id: clId } : { type: "none" });
  }, [currentAssignment.courseId, currentAssignment.classId, state]);

  // Filter classes by selected course when course-class links have been set up
  const selectedCourseId = courseVal.type === "existing" ? courseVal.id : null;
  const anyClassHasLinks = classes.some((c) => (c.courseIds?.length ?? 0) > 0);
  const filteredClasses =
    selectedCourseId != null && anyClassHasLinks
      ? classes.filter((c) => c.courseIds?.includes(selectedCourseId))
      : classes;
  const noLinkedClasses =
    selectedCourseId != null && anyClassHasLinks && filteredClasses.length === 0;
  const displayClasses = noLinkedClasses ? classes : filteredClasses;

  const handleApply = async () => {
    setIsSaving(true);
    try {
      let resolvedCourseId: number | null = null;
      let resolvedClassId:  number | null = null;

      if (courseVal.type === "existing") {
        resolvedCourseId = courseVal.id;
      } else if (courseVal.type === "new") {
        const created = await createCourse.mutateAsync({
          data: { name: courseVal.name, subtitle: null, color: "#94a3b8" },
        });
        resolvedCourseId = created.id;
        queryClient.invalidateQueries({ queryKey: ["/api/courses"] });
      }

      if (classVal.type === "existing") {
        resolvedClassId = classVal.id;
      } else if (classVal.type === "new") {
        const created = await createClass.mutateAsync({
          data: { name: classVal.name, yearLevel: null, schoolYear: null, courseIds: [] },
        });
        resolvedClassId = created.id;
        queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      }

      onSave(resolvedCourseId, resolvedClassId);
      onClose();
    } catch (err) {
      toast({
        title: "Couldn't save period assignment",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={() => { if (!isSaving) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">{state?.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>
              Subject{" "}
              <span className="text-xs font-normal text-muted-foreground">— what you teach</span>
            </Label>
            <InlineCombobox
              value={courseVal}
              onChange={setCourseVal}
              options={courses}
              placeholder="No subject"
              createPrefix="subject"
            />
          </div>
          <div className="space-y-1.5">
            <Label>
              Class / Group{" "}
              <span className="text-xs font-normal text-muted-foreground">— who you teach</span>
              {selectedCourseId != null && anyClassHasLinks && !noLinkedClasses && (
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  — filtered by course
                </span>
              )}
            </Label>
            {noLinkedClasses && (
              <p className="text-xs text-muted-foreground italic">
                No classes linked to this course — showing all
              </p>
            )}
            <InlineCombobox
              value={classVal}
              onChange={setClassVal}
              options={displayClasses}
              placeholder="No class / group"
              createPrefix="class"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onSave(null, null); onClose(); }}
            disabled={isSaving}
          >
            Clear
          </Button>
          <Button variant="outline" size="sm" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleApply} disabled={isSaving}>
            {isSaving ? "Saving…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sortable Slot (per-day card) ─────────────────────────────────────────────

function SortableSlot({
  slot, courseMap, classMap, onSlotClick, onDeleteSlot, onUpdateLabel, isDragOverlay,
  onMoveUp, onMoveDown, isFirst, isLast,
}: {
  slot: DaySlot;
  courseMap: Map<number, { id: number; name: string; color: string }>;
  classMap:  Map<number, { id: number; name: string }>;
  onSlotClick:   () => void;
  onDeleteSlot:  () => void;
  onUpdateLabel: (label: string) => void;
  isDragOverlay?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: slot.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const base = `flex items-center gap-1 group px-1.5 ${
    isDragOverlay ? "bg-white shadow-xl rounded-lg border border-gray-200 opacity-95 py-1" : "py-0.5"
  }`;

  if (slot.type === "break") {
    return (
      <div ref={setNodeRef} style={style} className={base}>
        <div
          {...attributes} {...listeners}
          style={{ touchAction: "none" }}
          className="flex-shrink-0 text-gray-200 hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors p-0.5 hidden sm:flex"
        >
          <GripVertical className="h-3.5 w-3.5" />
        </div>
        <div className="flex flex-col sm:hidden flex-shrink-0">
          <button onClick={onMoveUp} disabled={isFirst} className="text-gray-200 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronUp className="h-3 w-3" /></button>
          <button onClick={onMoveDown} disabled={isLast} className="text-gray-200 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronDown className="h-3 w-3" /></button>
        </div>
        <input
          className="flex-1 text-[11px] text-gray-400 font-semibold uppercase tracking-wider text-center bg-transparent border-none outline-none min-w-0 hover:text-gray-600 focus:text-gray-700 py-1"
          value={slot.label}
          onChange={(e) => onUpdateLabel(e.target.value)}
          title="Click to rename"
        />
        <button
          onClick={onDeleteSlot}
          className="flex-shrink-0 text-gray-200 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-0.5"
          title="Remove"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  const course     = slot.courseId ? courseMap.get(slot.courseId) : null;
  const cls        = slot.classId  ? classMap.get(slot.classId)   : null;
  const bg         = course?.color ?? null;
  const isClassOnly = !course && !!cls;

  return (
    <div ref={setNodeRef} style={style} className={base}>
      <div
        {...attributes} {...listeners}
        style={{ touchAction: "none" }}
        className="flex-shrink-0 text-gray-200 hover:text-gray-400 cursor-grab active:cursor-grabbing transition-colors p-0.5 hidden sm:flex"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </div>
      <div className="flex flex-col sm:hidden flex-shrink-0">
        <button onClick={onMoveUp} disabled={isFirst} className="text-gray-200 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronUp className="h-3 w-3" /></button>
        <button onClick={onMoveDown} disabled={isLast} className="text-gray-200 hover:text-gray-500 disabled:opacity-20 p-0.5"><ChevronDown className="h-3 w-3" /></button>
      </div>
      <button
        onClick={onSlotClick}
        className={`flex-1 rounded-md py-2 px-1.5 text-center text-xs font-semibold transition-all min-w-0 truncate ${
          bg
            ? "text-white shadow-sm hover:opacity-90 active:scale-95"
            : isClassOnly
              ? "text-gray-700 bg-gray-200 border border-gray-300 hover:bg-gray-300 active:scale-95"
              : "text-gray-300 bg-gray-50 border-2 border-dashed border-gray-200 hover:border-primary/40 hover:text-primary/50"
        }`}
        style={bg ? { backgroundColor: bg } : {}}
        title={bg || isClassOnly ? "Click to change" : "Click to assign"}
      >
        {course
          ? `${course.name}${cls ? ` · ${cls.name}` : ""}`
          : cls
            ? cls.name
            : <Plus className="h-3.5 w-3.5 mx-auto opacity-40" />}
      </button>
      <button
        onClick={onDeleteSlot}
        className="flex-shrink-0 text-gray-200 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-0.5"
        title="Remove"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Day Column ───────────────────────────────────────────────────────────────

function DayColumn({
  day, slots, courseMap, classMap,
  onSlotClick, onDeleteSlot, onUpdateLabel, onReorder, onAdd, onApplyToAll,
}: {
  day: Day;
  slots: DaySlot[];
  courseMap: Map<number, { id: number; name: string; color: string }>;
  classMap:  Map<number, { id: number; name: string }>;
  onSlotClick:   (slotId: string) => void;
  onDeleteSlot:  (slotId: string) => void;
  onUpdateLabel: (slotId: string, label: string) => void;
  onReorder:     (newSlots: DaySlot[]) => void;
  onAdd:         (type: "period" | "break") => void;
  onApplyToAll:  (slots: DaySlot[]) => void;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      const oldIdx = slots.findIndex((s) => s.id === active.id);
      const newIdx = slots.findIndex((s) => s.id === over.id);
      onReorder(arrayMove(slots, oldIdx, newIdx));
    }
  };

  const activeSlot = activeId ? slots.find((s) => s.id === activeId) : null;
  const slotProps  = { courseMap, classMap, onSlotClick: () => {}, onDeleteSlot: () => {}, onUpdateLabel: () => {} };

  return (
    <div className="flex flex-col flex-1 min-w-0">
      {/* Day header */}
      <div className="flex flex-col items-center justify-center gap-0.5 py-2.5 px-1 border-b border-gray-100 bg-gray-50/50">
        <span className="text-sm font-semibold text-gray-600">{DAY_LABELS[day]}</span>
        <button
          onClick={() => onApplyToAll(slots)}
          className="text-[10px] text-gray-300 hover:text-primary transition-colors flex items-center gap-0.5"
          title="Copy this day's structure to all other days"
        >
          <CopyCheck className="h-2.5 w-2.5" />
          Apply to all
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(e) => setActiveId(e.active.id as string)}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={slots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div className="flex-1 divide-y divide-gray-50 py-0.5">
            {slots.map((slot, idx) => (
              <SortableSlot
                key={slot.id}
                slot={slot}
                courseMap={courseMap}
                classMap={classMap}
                onSlotClick={() => onSlotClick(slot.id)}
                onDeleteSlot={() => onDeleteSlot(slot.id)}
                onUpdateLabel={(label) => onUpdateLabel(slot.id, label)}
                isFirst={idx === 0}
                isLast={idx === slots.length - 1}
                onMoveUp={() => onReorder(arrayMove(slots, idx, idx - 1))}
                onMoveDown={() => onReorder(arrayMove(slots, idx, idx + 1))}
              />
            ))}
            {slots.length === 0 && (
              <div className="py-6 text-center text-xs text-gray-300">No slots</div>
            )}
          </div>
        </SortableContext>

        <DragOverlay>
          {activeSlot && (
            <SortableSlot
              slot={activeSlot}
              {...slotProps}
              isDragOverlay
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* Add buttons */}
      <div className="flex border-t border-gray-100">
        <button
          onClick={() => onAdd("period")}
          className="flex-1 text-[11px] text-gray-400 hover:text-primary py-2 hover:bg-primary/5 transition-colors border-r border-gray-100"
        >
          + Period
        </button>
        <button
          onClick={() => onAdd("break")}
          className="flex-1 text-[11px] text-gray-400 hover:text-gray-600 py-2 hover:bg-gray-50 transition-colors"
        >
          + Break
        </button>
      </div>
    </div>
  );
}

// ─── Timetable Grid ───────────────────────────────────────────────────────────

function TimetableGrid({
  columns, courses, classes, onSlotClick, onDeleteSlot, onUpdateLabel, onReorderDay, onAddSlot, onApplyToAll,
}: {
  columns:       DayColumns;
  courses:       { id: number; name: string; color: string }[];
  classes:       { id: number; name: string }[];
  onSlotClick:   (day: Day, slotId: string) => void;
  onDeleteSlot:  (day: Day, slotId: string) => void;
  onUpdateLabel: (day: Day, slotId: string, label: string) => void;
  onReorderDay:  (day: Day, newSlots: DaySlot[]) => void;
  onAddSlot:     (day: Day, type: "period" | "break") => void;
  onApplyToAll:  (sourceDay: Day, slots: DaySlot[]) => void;
}) {
  const courseMap = new Map(courses.map((c) => [c.id, c]));
  const classMap  = new Map(classes.map((c) => [c.id, c]));

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white" style={{ minWidth: 720 }}>
      <div className="flex divide-x divide-gray-100">
        {DAYS.map((day) => (
          <DayColumn
            key={day}
            day={day}
            slots={columns[day] ?? []}
            courseMap={courseMap}
            classMap={classMap}
            onSlotClick={(slotId) => onSlotClick(day, slotId)}
            onDeleteSlot={(slotId) => onDeleteSlot(day, slotId)}
            onUpdateLabel={(slotId, label) => onUpdateLabel(day, slotId, label)}
            onReorder={(newSlots) => onReorderDay(day, newSlots)}
            onAdd={(type) => onAddSlot(day, type)}
            onApplyToAll={(slots) => onApplyToAll(day, slots)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Copy-from Dialog ─────────────────────────────────────────────────────────

function CopyFromDialog({ open, onClose, targetTimetableId, currentTimetableName }: {
  open: boolean;
  onClose: () => void;
  targetTimetableId: number;
  currentTimetableName: string;
}) {
  const { data: allTimetables = [] } = useListTimetables();
  const copyFrom = useCopyTimetableFrom();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [sourceId, setSourceId] = useState<string>("");

  const otherTimetables = (allTimetables as any[]).filter((t) => t.id !== targetTimetableId);

  const handleCopy = async () => {
    if (!sourceId) return;
    const source = otherTimetables.find((t) => String(t.id) === sourceId);
    try {
      await copyFrom.mutateAsync({ timetableId: targetTimetableId, sourceTimetableId: Number(sourceId) });
      queryClient.invalidateQueries({ queryKey: getGetTimetableQueryKey(targetTimetableId) });
      queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      toast({ title: `Timetable copied from "${source?.name ?? "diary"}"` });
      setSourceId("");
      onClose();
    } catch (err) {
      toast({
        title: "Couldn't copy the timetable",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy timetable from another diary</DialogTitle>
          <DialogDescription>
            Replaces the current structure of <strong>{currentTimetableName}</strong> with the
            period layout and course assignments from the selected diary. Lesson notes are not affected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {otherTimetables.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No other diaries to copy from. Create another diary first.
            </p>
          ) : (
            <div className="space-y-1.5">
              <Label>Copy structure from</Label>
              <Select value={sourceId} onValueChange={setSourceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a diary…" />
                </SelectTrigger>
                <SelectContent>
                  {otherTimetables.map((t: any) => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      <span>{t.name}</span>
                      {t.archived && <span className="ml-2 text-xs text-gray-400">(archived)</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleCopy} disabled={!sourceId || copyFrom.isPending || otherTimetables.length === 0}>
            {copyFrom.isPending ? "Copying…" : "Copy timetable"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Timetable Editor ─────────────────────────────────────────────────────────

function TimetableEditor({ timetableId, onDone }: { timetableId: number; onDone: () => void }) {
  const { data: timetable, isLoading } = useGetTimetable(timetableId);
  const { data: courses = [] }         = useListCourses();
  const { data: classes = [] }         = useListClasses();
  const createWeek = useCreateTimetableWeek();
  const saveSlots  = useSaveTimetableSlots();
  const { toast }  = useToast();
  const queryClient = useQueryClient();

  const [weekColumns, setWeekColumns] = useState<Record<string, DayColumns> | null>(null);
  const [activeWeek, setActiveWeek]   = useState<"A" | "B">("A");
  const [useABWeeks, setUseABWeeks]   = useState(false);
  const [isSaving, setIsSaving]       = useState(false);
  const [isDirty, setIsDirty]         = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  const [editCell, setEditCell]                   = useState<EditCellState | null>(null);
  const [editCellAssignment, setEditCellAssignment] = useState<{ courseId: number | null; classId: number | null }>({ courseId: null, classId: null });
  const [copyDialogOpen, setCopyDialogOpen]         = useState(false);

  // Build per-day columns from server data
  useEffect(() => {
    if (!timetable || weekColumns !== null) return;
    const cols: Record<string, DayColumns> = {};
    if (timetable.weeks?.length) {
      for (const week of timetable.weeks) {
        cols[week.weekLabel ?? "A"] = buildColumnsFromSlots(week.slots ?? []);
      }
    }
    if (!cols.A) cols.A = defaultDayColumns();
    setWeekColumns(cols);
    setUseABWeeks(Object.keys(cols).length >= 2);
  }, [timetable, weekColumns]);

  const currentCols = (weekColumns ?? { A: defaultDayColumns() })[activeWeek] ?? defaultDayColumns();

  // Detect risky structural changes vs the saved timetable: course reassignment on an existing
  // period, or removal of a period slot. Used to show a helpful guidance banner while editing.
  // No diary queries — compares client state vs already-fetched timetable detail only.
  const hasRiskyChanges = weekColumns != null && timetable != null && (() => {
    for (const week of (timetable.weeks ?? [])) {
      const wl = week.weekLabel ?? "A";
      const currCols = weekColumns[wl];
      if (!currCols) continue;
      for (const day of DAYS) {
        const origSlots = ((week.slots ?? []) as Array<{ day: string; position: number; slotType: string; courseId: number | null; classId: number | null }>)
          .filter((s) => s.day === day)
          .sort((a, b) => a.position - b.position);
        const currSlots = currCols[day] ?? [];
        // Period removed?
        const origPeriods = origSlots.filter((s) => s.slotType === "period").length;
        const currPeriods = currSlots.filter((s) => s.type === "period").length;
        if (currPeriods < origPeriods) return true;
        // Course or class reassigned at same position?
        for (let i = 0; i < Math.min(origSlots.length, currSlots.length); i++) {
          const orig = origSlots[i];
          const curr = currSlots[i];
          if (
            orig.slotType === "period" &&
            curr.type === "period" &&
            (orig.courseId !== curr.courseId || orig.classId !== curr.classId)
          ) return true;
        }
      }
    }
    return false;
  })();

  const updateDayColumn = (day: Day, slots: DaySlot[]) => {
    setWeekColumns((prev) => {
      const base = prev ?? { A: defaultDayColumns() };
      return { ...base, [activeWeek]: { ...(base[activeWeek] ?? defaultDayColumns()), [day]: slots } };
    });
    setIsDirty(true);
  };

  const handleSlotClick = (day: Day, slotId: string) => {
    const slot = currentCols[day]?.find((s) => s.id === slotId);
    if (!slot || slot.type === "break") return;
    setEditCellAssignment({ courseId: slot.courseId, classId: slot.classId });
    setEditCell({ day, slotId, title: `${DAY_LABELS[day]} — ${slot.label}` });
  };

  const handleSlotSave = (courseId: number | null, classId: number | null) => {
    if (!editCell) return;
    updateDayColumn(
      editCell.day,
      (currentCols[editCell.day] ?? []).map((s) =>
        s.id === editCell.slotId ? { ...s, courseId, classId } : s
      ),
    );
  };

  const deleteSlot = (day: Day, slotId: string) =>
    updateDayColumn(day, renumberPeriods((currentCols[day] ?? []).filter((s) => s.id !== slotId)));

  const updateSlotLabel = (day: Day, slotId: string, label: string) =>
    updateDayColumn(day, (currentCols[day] ?? []).map((s) => s.id === slotId ? { ...s, label } : s));

  const addSlot = (day: Day, type: "period" | "break") => {
    const existing = currentCols[day] ?? [];
    const periods  = existing.filter((s) => s.type === "period").length;
    const breaks   = existing.filter((s) => s.type === "break").length;
    const label    = type === "period"
      ? `Period ${periods + 1}`
      : breaks === 0 ? "Recess" : breaks === 1 ? "Lunch" : breaks === 2 ? "Huddle" : "Break";
    updateDayColumn(day, [...existing, { id: makeId(), type, label, courseId: null, classId: null }]);
  };

  const applyToAll = (sourceDay: Day, slots: DaySlot[]) => {
    // Copy structure (type + label) to all other days; clear course/class assignments
    setWeekColumns((prev) => {
      const base = prev ?? { A: defaultDayColumns() };
      const currentWeek = base[activeWeek] ?? defaultDayColumns();
      const updated: DayColumns = { ...currentWeek };
      for (const day of DAYS) {
        if (day === sourceDay) continue;
        updated[day] = slots.map((s) => ({
          ...s,
          id: makeId(),
          courseId: null,
          classId: null,
        }));
      }
      return { ...base, [activeWeek]: updated };
    });
    setIsDirty(true);
    toast({ title: `${DAY_LABELS[sourceDay]} structure applied to all days` });
  };

  const toggleABWeeks = (on: boolean) => {
    setUseABWeeks(on);
    if (on && weekColumns && !weekColumns.B) {
      const aCols = weekColumns.A ?? defaultDayColumns();
      const bCols: DayColumns = Object.fromEntries(
        DAYS.map((day) => [day, aCols[day].map((s) => ({ ...s, id: makeId(), courseId: null, classId: null }))])
      ) as DayColumns;
      setWeekColumns((prev) => ({ ...prev!, B: bCols }));
    }
    setIsDirty(true);
  };

  const handleDiscard = () => {
    setWeekColumns(null);
    setIsDirty(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const cols       = weekColumns ?? { A: defaultDayColumns() };
      const weekLabels = useABWeeks ? ["A", "B"] : ["A"];
      const existingWeekMap = new Map(timetable?.weeks?.map((w: any) => [w.weekLabel, w.id]) ?? []);

      for (const label of weekLabels) {
        let weekId = existingWeekMap.get(label);
        if (!weekId) {
          const week = await createWeek.mutateAsync({ timetableId, data: { weekLabel: label } });
          weekId = week.id;
        }
        const dayCols = cols[label] ?? cols.A;
        const slotsPayload = DAYS.flatMap((day) =>
          (dayCols[day] ?? []).map((slot, idx) => ({
            day,
            position: idx,
            slotType: slot.type,
            label:    slot.label,
            courseId: slot.type === "period" ? slot.courseId : null,
            classId:  slot.type === "period" ? slot.classId  : null,
          }))
        );
        await saveSlots.mutateAsync({ timetableId, weekId, data: { slots: slotsPayload } });
      }

      queryClient.invalidateQueries({ queryKey: getGetTimetableQueryKey(timetableId) });
      queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      setIsDirty(false);
      toast({ title: "Timetable saved!" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Couldn't save the timetable",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || weekColumns === null) {
    return (
      <div className="flex items-center justify-center p-16 text-gray-400">
        <div className="text-sm">Loading timetable…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0 flex-wrap gap-y-2">
        <button
          onClick={onDone}
          className="text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0"
          title="Back"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>

        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900 truncate">{timetable?.name}</h2>
          {timetable?.startDate && timetable?.endDate && (
            <p className="text-xs text-gray-400">
              {format(new Date(timetable.startDate + "T00:00:00"), "d MMM yyyy")} –{" "}
              {format(new Date(timetable.endDate   + "T00:00:00"), "d MMM yyyy")}
            </p>
          )}
        </div>

        {/* A/B week toggle */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => { toggleABWeeks(false); setActiveWeek("A"); }}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
              !useABWeeks ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Single week
          </button>
          <button
            onClick={() => toggleABWeeks(true)}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
              useABWeeks ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            A / B weeks
          </button>
        </div>

        {useABWeeks && (
          <Select value={activeWeek} onValueChange={(v) => setActiveWeek(v as "A" | "B")}>
            <SelectTrigger className="w-28 h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="A">Week A</SelectItem>
              <SelectItem value="B">Week B</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Button
          variant="outline" size="sm"
          className="gap-1.5 text-gray-600"
          onClick={() => setCopyDialogOpen(true)}
          title="Copy timetable from another diary"
        >
          <Copy className="h-3.5 w-3.5" />
          Copy from…
        </Button>
        <Button
          variant="outline" size="sm"
          disabled={!isDirty}
          onClick={handleDiscard}
          className="text-gray-600"
        >
          Discard
        </Button>
        <Button size="sm" onClick={() => setSaveConfirmOpen(true)} disabled={isSaving} className="min-w-16" data-write-action>
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-6 bg-gray-50">

        {/* Subject/class structure explanation */}
        <div className="mb-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 space-y-2">
          <p>
            <strong>Subjects</strong> = what you teach — for example, Maths, Science, PDHPE or SLR.
            {" "}<strong>Classes/Groups</strong> = who you teach — for example, 10.1, 10.2, Year 5 or Support Group.
          </p>
          <ul className="list-disc list-inside space-y-0.5 text-blue-700">
            <li>Use <strong>subjects only</strong> if that is enough for your teaching setup.</li>
            <li>Use <strong>classes only</strong> if you mainly organise by group.</li>
            <li>Use <strong>subjects and classes</strong> if you teach the same subject to more than one group.</li>
          </ul>
        </div>

        {(courses as any[]).length === 0 && (
          <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            Add a <strong>Class</strong>, <strong>Subject</strong>, or both to timetable your teaching. You can also leave periods blank and fill them in later.
          </div>
        )}

        {isDirty && hasRiskyChanges && (
          <div className="mb-4 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <strong>Heads up:</strong> Changing or deleting periods that already have lesson notes can affect historical accuracy.
            For timetable changes from a certain date forward, creating a new timetable is usually safer.
          </div>
        )}

        <TimetableGrid
          columns={currentCols}
          courses={courses as any[]}
          classes={classes as any[]}
          onSlotClick={handleSlotClick}
          onDeleteSlot={deleteSlot}
          onUpdateLabel={updateSlotLabel}
          onReorderDay={(day, newSlots) => updateDayColumn(day, renumberPeriods(newSlots))}
          onAddSlot={addSlot}
          onApplyToAll={applyToAll}
        />
      </div>

      {/* Cell edit dialog */}
      <CellEditDialog
        state={editCell}
        onClose={() => setEditCell(null)}
        onSave={handleSlotSave}
        courses={courses as any[]}
        classes={classes as any[]}
        currentAssignment={editCellAssignment}
      />

      {/* Copy-from dialog */}
      <CopyFromDialog
        open={copyDialogOpen}
        onClose={() => { setCopyDialogOpen(false); setWeekColumns(null); }}
        targetTimetableId={timetableId}
        currentTimetableName={timetable?.name ?? ""}
      />

      {/* Save confirmation */}
      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save timetable changes?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Changing this timetable changes the whole timetable, including dates that have
                  already passed.
                </p>
                {hasRiskyChanges && (
                  <p>
                    If you change or remove periods, subjects or classes, diary and lesson content
                    connected to the old setup may no longer appear or be included in exports.
                  </p>
                )}
                <p>
                  For a one-off or temporary change, customise the day instead. For an ongoing
                  change, end this timetable on the last day the old timetable applies, then create
                  a new timetable for the new schedule.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setSaveConfirmOpen(false); handleSave(); }}>
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── New Timetable Dialog ─────────────────────────────────────────────────────

function NewTimetableDialog({
  open, onClose, onCreated, existingTimetables,
}: {
  open: boolean; onClose: () => void; onCreated: (id: number) => void;
  existingTimetables?: { startDate?: string | null; endDate?: string | null; name?: string | null }[];
}) {
  const createTimetable = useCreateTimetable();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName]           = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Warn about date overlap with existing timetables (but don't block)
    if (startDate && endDate && existingTimetables?.length) {
      const overlap = existingTimetables.find((t) => {
        if (!t.startDate || !t.endDate) return false;
        return startDate <= t.endDate && endDate >= t.startDate;
      });
      if (overlap) {
        toast({
          title: "This diary overlaps with another diary",
          description: `"${overlap.name ?? "Another diary"}" already covers some of these dates. You can create overlapping diaries if you need to, but both diaries may be available for the same dates. This makes it easier to accidentally write notes in the wrong diary. If this is an ongoing timetable change, it is usually safer to end the old diary before the new one starts.`,
          variant: "destructive",
        });
        return;
      }
    }

    try {
      const t = await createTimetable.mutateAsync({ data: { name, startDate, endDate } });
      queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      toast({ title: "Timetable created" });
      onCreated(t.id);
      onClose();
    } catch (err) {
      toast({
        title: "Couldn't create the timetable",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Timetable</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Term 2 2026" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start date *</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>End date *</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createTimetable.isPending} data-write-action>
              {createTimetable.isPending ? "Creating…" : "Create & configure"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TimetableSettings() {
  const { data: timetables, isLoading } = useListTimetables();
  const deleteTimetable = useDeleteTimetable();
  const { toast }       = useToast();
  const queryClient     = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editConfirmId, setEditConfirmId] = useState<number | null>(null);
  const [isNewOpen, setIsNewOpen] = useState(false);

  if (editingId !== null) {
    return (
      <div className="flex flex-col h-full">
        <TimetableEditor timetableId={editingId} onDone={() => setEditingId(null)} />
      </div>
    );
  }

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this timetable and all its diary entries?")) return;
    await deleteTimetable.mutateAsync({ timetableId: id });
    queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
    toast({ title: "Timetable deleted" });
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Settings2 className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Timetable Settings</h1>
        </div>
        <Button className="gap-2" onClick={() => setIsNewOpen(true)} data-write-action>
          <Plus className="h-4 w-4" />New Timetable
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        Set up your weekly schedule — which subject and class runs in each period, for each day.
      </p>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(2)].map((_, i) => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      )}

      {!isLoading && (!timetables || timetables.length === 0) && (
        <div className="py-20 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <Settings2 className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">You haven't set up a teaching timetable yet.</p>
          <Button onClick={() => setIsNewOpen(true)} data-write-action>Set up teaching timetable</Button>
        </div>
      )}

      {(() => {
        const allTimetables = (timetables as any[]) ?? [];
        const activeTimetables   = allTimetables.filter((t) => !t.archived);
        const archivedTimetables = allTimetables.filter((t) =>  t.archived);

        const TimetableRow = ({ timetable, archived }: { timetable: any; archived: boolean }) => (
          <div
            key={timetable.id}
            className={`border rounded-xl p-5 flex items-center justify-between transition-shadow group ${
              archived
                ? "bg-gray-50 border-gray-200"
                : "bg-white border-gray-200 hover:shadow-sm"
            }`}
          >
            <div>
              <div className="flex items-center gap-2">
                <h3 className={`font-bold ${archived ? "text-gray-500" : "text-gray-900"}`}>
                  {timetable.name}
                </h3>
                {archived && (
                  <span className="text-xs bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded font-medium">
                    Archived
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-400 mt-0.5">
                {timetable.startDate && timetable.endDate
                  ? `${format(new Date(timetable.startDate + "T00:00:00"), "d MMM yyyy")} — ${format(new Date(timetable.endDate + "T00:00:00"), "d MMM yyyy")}`
                  : "No dates set"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!archived && (
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setEditConfirmId(timetable.id)}>
                  <Settings2 className="h-3.5 w-3.5" />
                  Edit
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              )}
              {!archived && (
                <Button
                  variant="ghost" size="icon"
                  className="h-8 w-8 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDelete(timetable.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        );

        return (
          <>
            <div className="space-y-3">
              {activeTimetables.map((t) => <TimetableRow key={t.id} timetable={t} archived={false} />)}
            </div>

            {archivedTimetables.length > 0 && (
              <>
                <div className="mt-8 mb-4 flex items-center gap-3 text-sm text-gray-400">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span>Archived timetables</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="space-y-3">
                  {archivedTimetables.map((t) => <TimetableRow key={t.id} timetable={t} archived={true} />)}
                </div>
              </>
            )}
          </>
        );
      })()}

      {/* Edit confirmation — shown before entering the editor */}
      <AlertDialog open={editConfirmId !== null} onOpenChange={(open) => { if (!open) setEditConfirmId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit this timetable?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Small edits — such as adding extra periods, renaming periods, adding rooms, or
                  linking subjects/classes — can usually be made safely here.
                </p>
                <p>
                  If your school timetable has changed from a certain date forward, we recommend
                  ending this timetable on the last day it applied and creating a new timetable
                  starting from the change date. This keeps your diary notes, lesson periods and
                  exports aligned for reporting.
                </p>
                <p>
                  For larger changes — such as changing which class or subject runs in a period, or
                  deleting periods that already have notes — creating a new timetable is usually
                  safer.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setEditConfirmId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setEditingId(editConfirmId); setEditConfirmId(null); }}>
              Continue editing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewTimetableDialog
        open={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onCreated={(id) => { setIsNewOpen(false); setEditingId(id); }}
        existingTimetables={timetables ?? []}
      />
    </div>
  );
}
